import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { supabase } from "../supabaseClient";
import { normalizeJob, normalizeTech } from "./helpers";
import { CURRENT_TOS_VERSION } from "./constants";

const pathToView = {
  '/': 'jobs', '/techs': 'techs', '/post-job': 'postJob',
  '/create-profile': 'techProfile', '/login': 'login', '/signup': 'signup',
  '/my-profile': 'myProfile', '/job-post-success': 'jobPostSuccess',
  '/forgot-password': 'forgotPassword',
};
const viewToPath = {
  jobs: '/', techs: '/techs', postJob: '/post-job', techProfile: '/create-profile',
  login: '/login', signup: '/signup', myProfile: '/my-profile', jobPostSuccess: '/job-post-success',
  forgotPassword: '/forgot-password',
};

export default function useAppState(userProp) {
  const navigate = useNavigate();
  const location = useLocation();
  const view = pathToView[location.pathname] || 'jobs';
  const setView = (v, opts = {}) => navigate(viewToPath[v] || '/', opts);

  const [activeJob, setActiveJob] = useState(null);
  const [activeTech, setActiveTech] = useState(null);
  const [openToWork, setOpenToWork] = useState(true);
  const [profileStep, _setProfileStep] = useState(1);
  const [maxProfileStep, setMaxProfileStep] = useState(1);
  const [postStep, _setPostStep] = useState(1);
  const [maxPostStep, setMaxPostStep] = useState(1);

  const setProfileStep = (s) => {
    const next = typeof s === 'function' ? s(profileStep) : s;
    _setProfileStep(next);
    setMaxProfileStep(prev => Math.max(prev, next));
  };
  const setPostStep = (s) => {
    const next = typeof s === 'function' ? s(postStep) : s;
    _setPostStep(next);
    setMaxPostStep(prev => Math.max(prev, next));
  };
  const [filterCert, setFilterCert] = useState("");
  const [filterLocation, setFilterLocation] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [errors, setErrors] = useState({});
  const [jobs, setJobs] = useState([]);
  const [techs, setTechs] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  const [user, setUser] = useState(userProp ?? null);
  const [authForm, setAuthForm] = useState({ email: '', password: '' });
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);
  const [authSuccessMsg, setAuthSuccessMsg] = useState('');
  const [accountType, setAccountType] = useState(null); // 'technician' or 'employer'
  const [isPaidEmployer, setIsPaidEmployer] = useState(false);
  const [showTosModal, setShowTosModal] = useState(false);

  const resetSuccess = location.state?.resetSuccess || false;

  // Redirect non-employers away from /techs
  useEffect(() => {
    if (location.pathname !== '/techs') return;
    if (accountType === 'employer') return;
    // Wait for accountType to resolve before redirecting authenticated users
    if (user && accountType === null) return;
    if (accountType === 'technician') navigate('/dashboard', { replace: true });
    else if (!user) navigate('/', { replace: true });
  }, [location.pathname, user, accountType, navigate]);

  const [myProfile, setMyProfile] = useState(null);
  const [myProfileLoading, setMyProfileLoading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadPaths, setUploadPaths] = useState({ resume: null, certs: [], hazwoper8: null, physical: null });
  const [submitError, setSubmitError] = useState('');
  const [paymentLoading, setPaymentLoading] = useState(false);

  const [profile, setProfile] = useState({
    name: "", email: "", location: "", uxoHours: "", travel: "Nationwide", summary: "",
    dodCerts: [],
    hazwoper40: false, hazwoper40Date: "", hazwoper8: false, hazwoper8Date: "", hazwoper8Supervisor: false,
    physicalCurrent: false, physicalDate: "", militaryEod: false, clearance: false,
    clearanceLevel: "", diveCert: false, driversLicense: false, cdl: false,
    firstAidCpr: false, firstAidCprDate: "",
    phone: "",
    hasLogbook: false,
    supervisoryHours: 0,
    osha10Construction: false,
    qualityCredential: "",
    jobRolePreference: "any", specificRoles: [],
  });
  const [jobPost, setJobPost] = useState({
    company: "", title: "", location: "", type: "Contract", salary: "", description: "",
    requiredCerts: [], preferredCerts: [],
  });

  useEffect(() => { setUser(userProp ?? null); }, [userProp]);

  const goToCreateProfile = () => {
    if (!user) navigate('/signup', { state: { returnTo: '/create-profile' } });
    else setView('techProfile');
  };

  useEffect(() => {
    const isExp = (d) => d && (new Date() - new Date(d)) / 864e5 > 365;
    const isSoon = (d) => { if (!d) return false; const diff = (new Date() - new Date(d)) / 864e5; return diff > 335 && diff <= 365; };
    const notes = [];
    if (profile.hazwoper8 && isSoon(profile.hazwoper8Date)) notes.push("\u26A0\uFE0F Your HAZWOPER 8-Hour Refresher expires within 30 days.");
    if (profile.physicalCurrent && isSoon(profile.physicalDate)) notes.push("\u26A0\uFE0F Your physical expires within 30 days.");
    if (profile.hazwoper8 && isExp(profile.hazwoper8Date)) notes.push("\uD83D\uDEAB Your HAZWOPER 8-Hour Refresher has expired.");
    if (profile.physicalCurrent && isExp(profile.physicalDate)) notes.push("\uD83D\uDEAB Your physical has expired.");
    setNotifications(notes);
  }, [profile.hazwoper8Date, profile.physicalDate, profile.hazwoper8, profile.physicalCurrent]);

  useEffect(() => {
    (async () => {
      setDataLoading(true);
      const [{ data: jobData }, { data: techData }] = await Promise.all([
        supabase.from('job_posts').select('*').eq('status', 'published').order('created_at', { ascending: false }),
        supabase.from('tech_profiles').select('*').eq('open_to_work', true),
      ]);
      setJobs((jobData || []).map(normalizeJob));
      setTechs((techData || []).map(normalizeTech));
      setDataLoading(false);
    })();
  }, []);

  // Deep-link to a specific job via ?job=<id> query param
  useEffect(() => {
    if (dataLoading || jobs.length === 0) return;
    const params = new URLSearchParams(location.search);
    const jobId = params.get('job');
    if (jobId && view === 'jobs') {
      const found = jobs.find(j => j.id === jobId);
      if (found) {
        setActiveJob(found);
        // Clean the URL param without triggering a navigation
        navigate('/', { replace: true });
      }
    }
  }, [dataLoading, jobs, location.search]); // eslint-disable-line

  useEffect(() => {
    if (view === 'techProfile' && !user) navigate('/signup', { state: { returnTo: '/create-profile' }, replace: true });
    if (view === 'techProfile' && user && accountType === 'employer') navigate('/employer-dashboard', { replace: true });
    if (view === 'postJob' && !user) navigate('/signup', { state: { accountType: 'employer', returnTo: '/post-job', message: 'Create an employer account to post jobs' }, replace: true });
  }, [view, user, accountType]); // eslint-disable-line

  useEffect(() => {
    if (view === 'postJob' && user) {
      supabase.from('employer_profiles').select('company_name').eq('user_id', user.id).single()
        .then(({ data }) => { if (data?.company_name) setJobPost(p => ({ ...p, company: p.company || data.company_name })); });
    }
  }, [view, user]); // eslint-disable-line

  const fetchMyProfile = async () => {
    if (!user) return;
    setMyProfileLoading(true);
    const { data } = await supabase.from('tech_profiles').select('*').eq('user_id', user.id).single();
    setMyProfile(data ? normalizeTech(data) : null);
    setMyProfileLoading(false);
  };

  useEffect(() => { if (user) fetchMyProfile(); else setMyProfile(null); }, [user]); // eslint-disable-line

  useEffect(() => {
    if (view === 'techProfile' && user) {
      supabase.from('tech_profiles').select('*').eq('user_id', user.id).single()
        .then(({ data }) => {
          if (data) {
            setProfile({
              name: data.name || '', email: data.email || '', location: data.location || '',
              uxoHours: data.uxo_hours || '', travel: data.travel || 'Nationwide', summary: data.summary || '',
              dodCerts: data.dod_certs || [],
              hazwoper40: data.hazwoper_40 || false,
              hazwoper40Date: data.hazwoper_40_date || '', hazwoper8: data.hazwoper_8 || false,
              hazwoper8Date: data.hazwoper_8_date || '', hazwoper8Supervisor: data.hazwoper_8_supervisor || false,
              physicalCurrent: data.physical_current || false,
              physicalDate: data.physical_date || '', militaryEod: data.military_eod || false,
              clearance: data.clearance || false, clearanceLevel: data.clearance_level || '',
              diveCert: data.dive_cert || false, driversLicense: data.drivers_license || false, cdl: data.cdl || false,
              firstAidCpr: data.first_aid_cpr || false, firstAidCprDate: data.first_aid_cpr_date || '',
              phone: data.phone || '',
              hasLogbook: data.has_logbook || false,
              supervisoryHours: data.supervisory_hours || 0,
              osha10Construction: data.osha_10_construction || false,
              qualityCredential: data.quality_credential || '',
              jobRolePreference: data.job_role_preference || 'any', specificRoles: data.specific_roles || [],
            });
            setOpenToWork(data.open_to_work ?? true);
            setMaxProfileStep(3); // existing profile — all steps are visitable
            setUploadPaths({ resume: data.resume_path || null, certs: data.cert_paths || [], hazwoper8: data.hazwoper8_cert_path || null, physical: data.physical_cert_path || null });
          }
        });
    }
  }, [view, user]); // eslint-disable-line

  const filteredJobs = jobs.filter(j => {
    const certMatch = filterCert ? j.requiredCerts.includes(filterCert) || j.preferredCerts.includes(filterCert) : true;
    const locMatch = filterLocation ? j.location.toLowerCase().includes(filterLocation.toLowerCase()) : true;
    return certMatch && locMatch;
  });

  const handleSubmitProfile = async () => {
    if (!user) { navigate('/signup', { state: { returnTo: '/create-profile' } }); return; }
    setSubmitError('');

    // Compute expiry dates from issue dates
    const addYears = (dateStr, years) => {
      if (!dateStr) return null;
      const d = new Date(dateStr);
      d.setFullYear(d.getFullYear() + years);
      return d.toISOString().split('T')[0];
    };
    const hazwoper8Expiry = profile.hazwoper8 ? addYears(profile.hazwoper8Date, 1) : null;
    const firstAidCprExpiry = profile.firstAidCpr ? addYears(profile.firstAidCprDate, 2) : null;

    // Parse UXO hours text to integer for career progression
    const totalFieldHours = parseInt(String(profile.uxoHours).replace(/[^0-9]/g, ''), 10) || 0;

    const { error } = await supabase.from('tech_profiles').upsert({
      name: profile.name, email: profile.email || user.email, location: profile.location,
      uxo_hours: profile.uxoHours, travel: profile.travel, summary: profile.summary,
      total_field_hours: totalFieldHours,
      dod_certs: profile.dodCerts,
      hazwoper_40: profile.hazwoper40,
      hazwoper_40_date: profile.hazwoper40Date || null, hazwoper_8: profile.hazwoper8,
      hazwoper_8_date: profile.hazwoper8Date || null,
      hazwoper_8_expiry: hazwoper8Expiry,
      hazwoper_8_supervisor: profile.hazwoper8Supervisor,
      physical_current: profile.physicalCurrent,
      physical_date: profile.physicalDate || null, military_eod: profile.militaryEod,
      clearance: profile.clearance, clearance_level: profile.clearanceLevel,
      dive_cert: profile.diveCert, drivers_license: profile.driversLicense,
      cdl: profile.cdl, first_aid_cpr: profile.firstAidCpr,
      first_aid_cpr_date: profile.firstAidCprDate || null,
      first_aid_cpr_expiry: firstAidCprExpiry,
      phone: profile.phone || null,
      has_logbook: profile.hasLogbook,
      supervisory_hours: parseInt(String(profile.supervisoryHours).replace(/[^0-9]/g, ''), 10) || 0,
      osha_10_construction: profile.osha10Construction,
      quality_credential: profile.qualityCredential || null,
      job_role_preference: profile.jobRolePreference,
      specific_roles: profile.jobRolePreference === 'specific' ? profile.specificRoles : [],
      open_to_work: openToWork, user_id: user.id,
    }, { onConflict: 'user_id' });
    if (error) setSubmitError("Something went wrong saving your profile. Please try again.");
    else {
      // Sync document expiration dates with updated cert dates
      const { data: prof } = await supabase.from('tech_profiles').select('id').eq('user_id', user.id).single();
      if (prof) {
        const docUpdates = [
          { type: 'hazwoper_8hr', date: profile.hazwoper8Date, years: 1 },
          { type: 'physical',     date: profile.physicalDate,  years: 1 },
          { type: 'first_aid_cpr', date: profile.firstAidCprDate, years: 2 },
        ];
        for (const { type, date, years } of docUpdates) {
          const newExpiry = date ? addYears(date, years) : null;
          await supabase.from('tech_documents')
            .update({ expires_at: newExpiry })
            .eq('tech_profile_id', prof.id)
            .eq('document_type', type);
        }

        // Re-verification trigger: if self-reported hours cross a threshold above verified hours
        const VERIFY_THRESHOLDS = [5640, 15040];
        const { data: currentProf } = await supabase.from('tech_profiles')
          .select('logbook_verified_hours, logbook_verification_status')
          .eq('id', prof.id).single();
        if (currentProf) {
          const verified = currentProf.logbook_verified_hours || 0;
          const crossedThreshold = VERIFY_THRESHOLDS.some(t => totalFieldHours >= t && verified < t);
          if (crossedThreshold && currentProf.logbook_verification_status !== 'pending') {
            // Only set to pending if a logbook has been uploaded
            const { count: logbookCount } = await supabase.from('tech_documents')
              .select('id', { count: 'exact', head: true })
              .eq('tech_profile_id', prof.id)
              .eq('document_type', 'logbook');
            if (logbookCount > 0) {
              await supabase.from('tech_profiles')
                .update({ logbook_verification_status: 'pending' })
                .eq('id', prof.id);
            }
          }
        }
      }

      const { data: techData } = await supabase.from('tech_profiles').select('*').eq('open_to_work', true);
      setTechs((techData || []).map(normalizeTech));
      // Check for pending job application redirect
      const pendingJobId = sessionStorage.getItem('pendingJobId');
      if (pendingJobId) {
        sessionStorage.removeItem('pendingJobId');
        window.location.href = `/?job=${pendingJobId}`;
      } else {
        navigate('/dashboard', { replace: true });
      }
    }
  };

  const handleSubmitJobPost = async () => {
    if (!user) { navigate('/login', { state: { returnTo: '/post-job' } }); return; }
    setSubmitError(''); setPaymentLoading(true);

    // Paid employers skip Stripe — publish directly
    if (isPaidEmployer) {
      const { error: insertError } = await supabase.from('job_posts').insert({
        company: jobPost.company, title: jobPost.title, location: jobPost.location,
        type: jobPost.type, salary: jobPost.salary, description: jobPost.description,
        required_certs: jobPost.requiredCerts, preferred_certs: jobPost.preferredCerts,
        status: 'published', user_id: user.id,
      });
      if (insertError) { setSubmitError('Something went wrong. Please try again.'); setPaymentLoading(false); return; }
      setPaymentLoading(false);
      setView('jobPostSuccess');
      return;
    }

    const { data: insertedJob, error: insertError } = await supabase.from('job_posts').insert({
      company: jobPost.company, title: jobPost.title, location: jobPost.location,
      type: jobPost.type, salary: jobPost.salary, description: jobPost.description,
      required_certs: jobPost.requiredCerts, preferred_certs: jobPost.preferredCerts,
      status: 'pending_payment', user_id: user.id,
    }).select('id').single();
    if (insertError || !insertedJob) { setSubmitError('Something went wrong. Please try again.'); setPaymentLoading(false); return; }
    const { data: checkoutData, error: fnError } = await supabase.functions.invoke('create-checkout-session', { body: { job_post_id: insertedJob.id } });
    if (fnError || !checkoutData?.url) { setSubmitError('Payment setup failed. Please contact support.'); setPaymentLoading(false); return; }
    window.location.href = checkoutData.url;
  };

  // Fetch account type on login and check ToS version
  useEffect(() => {
    if (!user) { setAccountType(null); setIsPaidEmployer(false); setShowTosModal(false); return; }
    supabase.from('user_accounts').select('account_type, is_paid_employer, tos_version_accepted').eq('user_id', user.id).single()
      .then(({ data }) => {
        if (data) {
          setAccountType(data.account_type);
          setIsPaidEmployer(data.is_paid_employer || false);
          // Check if ToS re-acceptance is needed
          if (data.tos_version_accepted !== CURRENT_TOS_VERSION) setShowTosModal(true);
          else setShowTosModal(false);
        } else {
          // Legacy user without account record — check if they have a tech profile
          supabase.from('tech_profiles').select('id').eq('user_id', user.id).single()
            .then(({ data: tp }) => setAccountType(tp ? 'technician' : null));
          setShowTosModal(true); // no record means no acceptance
        }
      });
  }, [user]); // eslint-disable-line

  const handleSignUp = async (selectedAccountType) => {
    setAuthLoading(true); setAuthError('');
    const acctType = selectedAccountType || 'technician';
    const { data, error } = await supabase.auth.signUp({
      email: authForm.email, password: authForm.password,
      options: { data: { account_type: acctType } },
    });
    setAuthLoading(false);
    if (error) { setAuthError(error.message); return; }
    // Insert user_accounts row with current ToS version
    if (data.user) {
      await supabase.from('user_accounts').upsert({
        user_id: data.user.id, account_type: acctType, tos_version_accepted: CURRENT_TOS_VERSION,
      }, { onConflict: 'user_id' });
      setAccountType(acctType);
    }
    const defaultRoute = acctType === 'employer' ? '/employer-onboarding' : '/create-profile';
    if (data.session) {
      setAuthForm({ email: '', password: '' });
      // pendingJobId stays in sessionStorage — picked up after profile completion
      window.location.href = location.state?.returnTo || defaultRoute;
    } else {
      setAuthSuccessMsg('Account created! Check your email to confirm, then log in.');
      navigate('/login', { replace: true, state: { returnTo: location.state?.returnTo || defaultRoute } });
    }
  };

  const handleLogin = async () => {
    setAuthLoading(true); setAuthError('');
    const { data: loginData, error } = await supabase.auth.signInWithPassword({ email: authForm.email, password: authForm.password });
    setAuthLoading(false);
    if (error) { setAuthError(error.message); return; }
    setAuthForm({ email: '', password: '' });
    // Check for pending job application redirect
    const pendingJobId = sessionStorage.getItem('pendingJobId');
    if (pendingJobId) {
      sessionStorage.removeItem('pendingJobId');
      window.location.href = `/?job=${pendingJobId}`;
      return;
    }
    // Determine destination
    let dest = location.state?.returnTo || '/dashboard';
    if (!location.state?.returnTo && loginData.user) {
      const { data: acct } = await supabase.from('user_accounts')
        .select('account_type').eq('user_id', loginData.user.id).maybeSingle();
      const acctType = acct?.account_type || loginData.user.user_metadata?.account_type;
      if (acctType === 'employer') dest = '/employer-dashboard';
    }
    // Full page navigation so auth state initializes cleanly
    window.location.href = dest;
  };

  const handleSignOut = async () => { await supabase.auth.signOut(); setUser(null); setMyProfile(null); setView('jobs'); };

  const handleToggleAvailability = async () => {
    if (!myProfile || !user) return;
    const newStatus = !myProfile.available;
    const { error } = await supabase.from('tech_profiles').update({ open_to_work: newStatus }).eq('user_id', user.id);
    if (!error) {
      setMyProfile(p => ({ ...p, available: newStatus }));
      const { data: techData } = await supabase.from('tech_profiles').select('*').eq('open_to_work', true);
      setTechs((techData || []).map(normalizeTech));
    }
  };

  return {
    view, setView, navigate,
    activeJob, setActiveJob, activeTech, setActiveTech,
    openToWork, setOpenToWork, profileStep, setProfileStep, maxProfileStep,
    postStep, setPostStep, maxPostStep, filterCert, setFilterCert,
    filterLocation, setFilterLocation, notifications,
    errors, setErrors, filteredJobs, techs, dataLoading,
    user, accountType, isPaidEmployer, showTosModal, setShowTosModal, authForm, setAuthForm, authError, authLoading, authSuccessMsg, resetSuccess,
    myProfile, myProfileLoading, uploadStatus, setUploadStatus,
    uploadPaths, setUploadPaths, submitError, paymentLoading,
    profile, setProfile, jobPost, setJobPost,
    goToCreateProfile, handleSubmitProfile, handleSubmitJobPost,
    handleSignUp, handleLogin, handleSignOut, handleToggleAvailability,
  };
}
